// Story 10.4 — Funnel chart por flow. Muestra count + dropoff entre steps.

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AnalyticsFunnelFlow } from '@/lib/analytics-types';

interface Props {
  flows: AnalyticsFunnelFlow[];
}

export function FunnelChart({ flows }: Props): React.ReactElement {
  if (flows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Sin ejecuciones de flow en el periodo.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {flows.map((flow) => {
        const data = flow.steps.map((s, idx) => ({
          ...s,
          label: idx === 0 ? s.stepId : `${s.stepId} (-${Math.round(s.dropOff * 100)}%)`,
        }));
        return (
          <Card key={flow.flowId}>
            <CardHeader>
              <CardTitle className="text-sm">Funnel: {flow.flowId}</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="h-[220px] w-full"
                role="img"
                aria-label={`Funnel del flow ${flow.flowId} con ${flow.steps.length} steps`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                  >
                    <CartesianGrid
                      stroke="var(--border)"
                      strokeDasharray="3 3"
                      horizontal={false}
                    />
                    <XAxis type="number" stroke="var(--muted-foreground)" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      stroke="var(--muted-foreground)"
                      tick={{ fontSize: 11 }}
                      width={140}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--card)',
                        border: '1px solid var(--border)',
                        fontSize: 12,
                      }}
                      formatter={(value: number, _name, item) => {
                        const dropOff = (item?.payload as { dropOff?: number } | undefined)
                          ?.dropOff;
                        return [
                          `${value} ejecuciones${dropOff ? ` (-${Math.round(dropOff * 100)}%)` : ''}`,
                          'Count',
                        ];
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="var(--primary)"
                      fillOpacity={0.85}
                      radius={[0, 2, 2, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
